/**
 * CuentaTab — Estado de cuenta (ledger) del paciente.
 *
 * Muestra:
 *   - 3 KPIs (saldo pendiente, total facturado, total pagado)
 *   - Lista de cargos con su estado de pago + acciones (pagar, cancelar)
 *   - Lista de pagos recientes con opción de anular
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Button, IconButton, Tooltip, Skeleton, Menu, MenuItem,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  AddCircleOutline as AddIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  TrendingUp as TrendingUpIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import DialogNuevoCargo from './DialogNuevoCargo';
import DialogNuevoPago from './DialogNuevoPago';

const API_URL = '/api';

const ESTADO_CONFIG = {
  pendiente: { label: 'Pendiente', color: '#b45309', bg: '#fef3c7', icon: <ScheduleIcon sx={{ fontSize: 12 }} /> },
  parcial:   { label: 'Parcial',   color: '#1e40af', bg: '#dbeafe', icon: <TrendingUpIcon sx={{ fontSize: 12 }} /> },
  pagado:    { label: 'Pagado',    color: '#15803d', bg: '#dcfce7', icon: <CheckIcon sx={{ fontSize: 12 }} /> },
  cancelado: { label: 'Cancelado', color: '#64748b', bg: '#f1f5f9', icon: <BlockIcon sx={{ fontSize: 12 }} /> },
};

const METODO_LABEL = {
  efectivo: 'Efectivo',
  tarjeta_debito: 'Tarjeta débito',
  tarjeta_credito: 'Tarjeta crédito',
  transferencia: 'Transferencia',
  cheque: 'Cheque',
  mercado_pago: 'Mercado Pago',
  stripe: 'Stripe',
  otro: 'Otro',
};

function fmtMoney(num, moneda = 'MXN') {
  const n = Number(num || 0);
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda, maximumFractionDigits: 2 }).format(n);
}

function fmtFecha(iso) {
  return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function CuentaTab({ paciente, expediente, userId }) {
  const [ledger, setLedger] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [dialogoCargo, setDialogoCargo] = useState(false);
  const [dialogoPago, setDialogoPago] = useState({ abierto: false, cargo: null });
  const [menuCargo, setMenuCargo] = useState({ anchor: null, cargo: null });

  const cargarLedger = useCallback(async () => {
    if (!paciente?.id) return;
    setCargando(true);
    try {
      const res = await fetch(
        `${API_URL}/emr/finanzas/pacientes/${paciente.id}/ledger`,
        { headers: { 'X-User-Id': userId } }
      );
      if (res.ok) {
        const data = await res.json();
        setLedger(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCargando(false);
    }
  }, [paciente?.id, userId]);

  useEffect(() => { cargarLedger(); }, [cargarLedger]);

  const handleAnularPago = async (pago) => {
    const motivo = window.prompt('Motivo de anulación:');
    if (!motivo) return;
    try {
      const res = await fetch(`${API_URL}/emr/finanzas/pagos/${pago.id}/anular`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ motivo }),
      });
      if (!res.ok) throw new Error(await res.text());
      cargarLedger();
    } catch (e) {
      alert('No se pudo anular: ' + e.message);
    }
  };

  const handleCancelarCargo = async (cargo) => {
    if (!window.confirm(`¿Cancelar el cargo "${cargo.descripcion}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/emr/finanzas/cargos/${cargo.id}`, {
        method: 'DELETE',
        headers: { 'X-User-Id': userId },
      });
      if (!res.ok) throw new Error(await res.text());
      cargarLedger();
    } catch (e) {
      alert('No se pudo cancelar: ' + e.message);
    } finally {
      setMenuCargo({ anchor: null, cargo: null });
    }
  };

  const handleImprimirRecibo = async (cargo) => {
    try {
      const res = await fetch(`${API_URL}/emr/finanzas/recibo/cargo/${cargo.id}`, {
        headers: { 'X-User-Id': userId },
      });
      if (!res.ok) throw new Error('No se pudo generar el recibo');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Liberar la URL después de un rato
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setMenuCargo({ anchor: null, cargo: null });
    }
  };

  if (cargando) {
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {[1,2,3].map(i => <Skeleton key={i} variant="rounded" height={88} sx={{ flex: 1, borderRadius: 3 }} />)}
        </Box>
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  const cargos = ledger?.cargos || [];
  const pagos = ledger?.pagos || [];
  const moneda = ledger?.moneda || 'MXN';

  return (
    <Box>
      {/* KPIs */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <KpiCard
          label="Saldo pendiente"
          valor={ledger?.saldo_pendiente}
          moneda={moneda}
          color={Number(ledger?.saldo_pendiente || 0) > 0 ? '#dc2626' : '#15803d'}
          bg={Number(ledger?.saldo_pendiente || 0) > 0 ? '#fef2f2' : '#dcfce7'}
          icon={<MoneyIcon />}
        />
        <KpiCard
          label="Total facturado"
          valor={ledger?.total_cargos}
          moneda={moneda}
          color="#1e3a5f"
          bg="#eff6ff"
          icon={<ReceiptIcon />}
        />
        <KpiCard
          label="Total pagado"
          valor={ledger?.total_pagado}
          moneda={moneda}
          color="#15803d"
          bg="#dcfce7"
          icon={<PaymentIcon />}
        />
      </Box>

      {/* Acciones */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setDialogoCargo(true)}
          sx={{
            textTransform: 'none', fontSize: 12.5, fontWeight: 700, borderRadius: 2, px: 2,
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            boxShadow: '0 4px 12px rgba(30,58,95,0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
          }}
        >
          Nuevo cargo
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PaymentIcon sx={{ fontSize: 16 }} />}
          onClick={() => setDialogoPago({ abierto: true, cargo: null })}
          sx={{
            textTransform: 'none', fontSize: 12.5, fontWeight: 700, borderRadius: 2, px: 2,
            color: '#15803d', borderColor: '#86efac',
            '&:hover': { borderColor: '#15803d', background: '#dcfce7' },
          }}
        >
          Registrar pago
        </Button>
      </Box>

      {/* CARGOS */}
      <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', mb: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ fontSize: 18, color: '#1e3a5f' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#1e3a5f', letterSpacing: 0.3, textTransform: 'uppercase' }}>
            Cargos
          </Typography>
          <Chip label={cargos.length} size="small" sx={{
            ml: 'auto', height: 20, fontSize: 11, fontWeight: 700,
            background: '#1e3a5f', color: '#fff',
          }} />
        </Box>

        {cargos.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              Sin cargos registrados
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>
              Usa "Nuevo cargo" para registrar el primero
            </Typography>
          </Box>
        ) : (
          cargos.map((c, i) => (
            <CargoFila
              key={c.id} cargo={c} moneda={moneda}
              esUltimo={i === cargos.length - 1}
              onPagar={(cargo) => setDialogoPago({ abierto: true, cargo })}
              onMenu={(e, cargo) => setMenuCargo({ anchor: e.currentTarget, cargo })}
            />
          ))
        )}
      </Paper>

      {/* PAGOS */}
      <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon sx={{ fontSize: 18, color: '#15803d' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#15803d', letterSpacing: 0.3, textTransform: 'uppercase' }}>
            Pagos
          </Typography>
          <Chip label={pagos.length} size="small" sx={{
            ml: 'auto', height: 20, fontSize: 11, fontWeight: 700,
            background: '#15803d', color: '#fff',
          }} />
        </Box>

        {pagos.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PaymentIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              Sin pagos registrados
            </Typography>
          </Box>
        ) : (
          pagos.map((p, i) => (
            <PagoFila
              key={p.id} pago={p} moneda={moneda}
              esUltimo={i === pagos.length - 1}
              onAnular={() => handleAnularPago(p)}
            />
          ))
        )}
      </Paper>

      {/* Menú contextual de cargo */}
      <Menu
        anchorEl={menuCargo.anchor}
        open={Boolean(menuCargo.anchor)}
        onClose={() => setMenuCargo({ anchor: null, cargo: null })}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <MenuItem
          onClick={() => handleImprimirRecibo(menuCargo.cargo)}
          sx={{ fontSize: 13, gap: 1 }}
        >
          <PrintIcon sx={{ fontSize: 16 }} /> Imprimir recibo
        </MenuItem>
        <MenuItem
          onClick={() => handleCancelarCargo(menuCargo.cargo)}
          sx={{ fontSize: 13, gap: 1, color: '#dc2626' }}
          disabled={Number(menuCargo.cargo?.pagado || 0) > 0}
        >
          <CancelIcon sx={{ fontSize: 16 }} /> Cancelar cargo
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <DialogNuevoCargo
        open={dialogoCargo}
        onClose={() => setDialogoCargo(false)}
        paciente={paciente}
        userId={userId}
        onCreado={() => { setDialogoCargo(false); cargarLedger(); }}
      />
      <DialogNuevoPago
        open={dialogoPago.abierto}
        onClose={() => setDialogoPago({ abierto: false, cargo: null })}
        paciente={paciente}
        cargoPreseleccionado={dialogoPago.cargo}
        cargosPendientes={cargos.filter(c => c.estado === 'pendiente' || c.estado === 'parcial')}
        userId={userId}
        onCreado={() => { setDialogoPago({ abierto: false, cargo: null }); cargarLedger(); }}
      />
    </Box>
  );
}

// ============================================================================
function KpiCard({ label, valor, moneda, color, bg, icon }) {
  return (
    <Paper elevation={0} sx={{
      p: 1.5, flex: 1, minWidth: 180, borderRadius: 3,
      background: bg, border: `1px solid ${color}33`,
      borderLeft: `4px solid ${color}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.4 }}>
        <Box sx={{ color, display: 'flex' }}>
          {React.cloneElement(icon, { sx: { fontSize: 14 } })}
        </Box>
        <Typography sx={{ fontSize: 10, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.1 }}>
        {fmtMoney(valor, moneda)}
      </Typography>
    </Paper>
  );
}

// ============================================================================
function CargoFila({ cargo, moneda, esUltimo, onPagar, onMenu }) {
  const cfg = ESTADO_CONFIG[cargo.estado] || ESTADO_CONFIG.pendiente;
  const tienePagosParciales = cargo.estado === 'parcial';

  return (
    <Box sx={{
      p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5,
      borderBottom: esUltimo ? 'none' : '1px solid #f1f5f9',
      '&:hover': { background: '#fafbfc' },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
          <Typography sx={{
            fontSize: 13.5, fontWeight: 700, color: '#1e293b',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%',
          }}>
            {cargo.descripcion}
          </Typography>
          <Chip
            icon={cfg.icon}
            label={cfg.label} size="small"
            sx={{
              height: 20, fontSize: 10, fontWeight: 700,
              background: cfg.bg, color: cfg.color,
              '& .MuiChip-icon': { color: cfg.color, ml: 0.4 },
            }}
          />
        </Box>
        <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
          {fmtFecha(cargo.fecha_emision)}
          {Number(cargo.cantidad) !== 1 && ` · ${cargo.cantidad} × ${fmtMoney(cargo.precio_unitario, moneda)}`}
          {Number(cargo.descuento) > 0 && ` · descuento ${fmtMoney(cargo.descuento, moneda)}`}
          {tienePagosParciales && ` · pagado ${fmtMoney(cargo.pagado, moneda)} de ${fmtMoney(cargo.total, moneda)}`}
        </Typography>
        {cargo.notas && (
          <Typography sx={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', mt: 0.3 }}>
            {cargo.notas}
          </Typography>
        )}
      </Box>

      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
          {fmtMoney(cargo.total, moneda)}
        </Typography>
        {Number(cargo.saldo) > 0 && cargo.estado !== 'cancelado' && (
          <Typography sx={{ fontSize: 11, color: '#dc2626', fontWeight: 700, mt: 0.2 }}>
            Saldo {fmtMoney(cargo.saldo, moneda)}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 0.3, flexShrink: 0 }}>
        {(cargo.estado === 'pendiente' || cargo.estado === 'parcial') && (
          <Tooltip title="Registrar pago">
            <Button
              size="small" variant="contained"
              onClick={() => onPagar(cargo)}
              sx={{
                minWidth: 0, px: 1, py: 0.4, fontSize: 11, textTransform: 'none', fontWeight: 700,
                background: '#15803d', borderRadius: 1.5,
                '&:hover': { background: '#166534' },
              }}
            >
              Pagar
            </Button>
          </Tooltip>
        )}
        <IconButton size="small" onClick={(e) => onMenu(e, cargo)}
          sx={{ p: 0.4, color: '#94a3b8' }}>
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

// ============================================================================
function PagoFila({ pago, moneda, esUltimo, onAnular }) {
  return (
    <Box sx={{
      p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
      borderBottom: esUltimo ? 'none' : '1px solid #f1f5f9',
      opacity: pago.anulado ? 0.5 : 1,
      '&:hover': { background: pago.anulado ? 'transparent' : '#f0fdf4' },
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: 1.5,
        background: pago.anulado ? '#f1f5f9' : '#dcfce7',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <PaymentIcon sx={{ fontSize: 16, color: pago.anulado ? '#94a3b8' : '#15803d' }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 13, fontWeight: 700, color: pago.anulado ? '#94a3b8' : '#1e293b',
          textDecoration: pago.anulado ? 'line-through' : 'none',
        }}>
          {METODO_LABEL[pago.metodo] || pago.metodo}
          {pago.referencia && (
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b', ml: 0.8 }}>
              · {pago.referencia}
            </Box>
          )}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
          {fmtFecha(pago.fecha_pago)}
          {pago.anulado && ` · anulado: ${pago.anulado_motivo || 'sin motivo'}`}
        </Typography>
      </Box>

      <Typography sx={{
        fontSize: 14, fontWeight: 800,
        color: pago.anulado ? '#94a3b8' : '#15803d',
        textDecoration: pago.anulado ? 'line-through' : 'none',
        flexShrink: 0,
      }}>
        {fmtMoney(pago.monto, moneda)}
      </Typography>

      {!pago.anulado && (
        <Tooltip title="Anular pago">
          <IconButton size="small" onClick={onAnular}
            sx={{ p: 0.4, color: '#94a3b8', '&:hover': { color: '#dc2626' } }}>
            <CancelIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
